import styles from '../Account.module.css';
import AccountCard, { InfoRow } from '../AccountCard';
import SectionHead from '../SectionHead';
import StatusBadge from '../StatusBadge';
import EmptyState from '../EmptyState';
import Icon from '../Icon';
import SupportCard from '../SupportCard';
import { formatMoney, formatDate, timeAgo } from '../../../services/billingService';

const QUICK_ACTIONS = [
  { key: 'subscription', icon: 'package',  title: 'Manage subscription', desc: 'Change your plan or billing cycle' },
  { key: 'usage',        icon: 'activity', title: 'Usage',               desc: 'Credits used and remaining' },
  { key: 'invoices',     icon: 'fileText', title: 'Invoices',            desc: 'View and download billing history' },
  { key: 'security',     icon: 'shield',   title: 'Security',            desc: 'Password and account safety' },
];

const TRIAL_PERKS = ['7-day free trial', 'No credit card required', 'Cancel anytime'];

function initials(name, email) {
  if (name) {
    const p = name.trim().split(/\s+/);
    return (p[0][0] + (p[1]?.[0] || '')).toUpperCase();
  }
  if (email) return email[0].toUpperCase();
  return null;
}

export default function OverviewSection({ billing, onNavigate, onChoosePlan, onContact }) {
  const { identity, subscription: sub, trial, status, credits } = billing || {};
  const name = identity?.name;
  const email = identity?.email;
  const isFounding = identity?.isFounding;
  const firstName = name ? name.split(' ')[0] : null;
  const hasIdentity = !!(name || email || identity?.company || identity?.role);
  const avatarText = initials(name, email);

  // Trial/renewal snapshot line shown inside the summary hero.
  let trialLine = null;
  if (trial?.status === 'pending') trialLine = `Free trial starts ${formatDate(trial.startsAt)} - no credit card required`;
  else if (trial?.status === 'active') trialLine = `${trial.daysLeft} ${trial.daysLeft === 1 ? 'day' : 'days'} left on your free trial`;
  else if (sub?.canceled) trialLine = `Access ends ${formatDate(trial?.endsAt)} - you won't be charged`;

  return (
    <>
      <SectionHead
        title={firstName ? `Welcome back, ${firstName}` : 'Overview'}
        subtitle="A summary of your account, plan and subscription status."
      />
      {billing?.fetchedAt && (
        <div className={styles.lastUpdated}>
          <Icon name="clock" size={12} /> Last updated {timeAgo(billing.fetchedAt)}
        </div>
      )}

      {/* ── Subscription summary hero ── */}
      {sub ? (
        <div className={styles.summaryCard}>
          <div className={styles.summaryTop}>
            <div style={{ minWidth: 0 }}>
              <div className={styles.summaryEyebrow}>Current plan</div>
              <div className={styles.summaryPlanName}>
                {isFounding ? `Founding ${sub.plan}` : sub.plan}
                <StatusBadge status={status} />
                {isFounding && <span className={styles.foundingBadge}>👑 Price locked</span>}
              </div>
            </div>
            <div className={styles.summaryPriceBox}>
              <span className={styles.summaryPrice}>{formatMoney(sub.price, sub.currency)}</span>
              <span className={styles.summaryPriceUnit}>{sub.unit}</span>
            </div>
          </div>

          <div className={styles.summaryStats}>
            <div className={styles.stat}>
              <span className={styles.statLabel}><Icon name="calendar" size={12} /> Billing cycle</span>
              <span className={styles.statValue}>{sub.billing}</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statLabel}>
                <Icon name="clock" size={12} /> {sub.canceled ? 'Access ends' : 'Billing starts'}
              </span>
              <span className={styles.statValue}>{formatDate(sub.canceled ? trial?.endsAt : sub.billingStartsAt)}</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statLabel}><Icon name="activity" size={12} /> Lead credits</span>
              <span className={styles.statValue}>
                {credits?.available
                  ? (credits.unlimited ? 'Unlimited' : `${credits.remaining} of ${credits.allowance} left`)
                  : `${sub.credits} leads`}
              </span>
            </div>
          </div>

          {trialLine && (
            <div className={styles.summaryTrial}>
              <span className={styles.summaryTrialIcon}><Icon name="zap" size={14} /></span>
              {trialLine}
            </div>
          )}

          <div className={styles.cardActions}>
            <button className={styles.btnPrimary} onClick={() => onNavigate('subscription')}>
              Manage subscription <Icon name="arrowRight" size={14} />
            </button>
            <button className={styles.btnGhost} onClick={() => onNavigate('billing')}>Billing details</button>
          </div>
        </div>
      ) : (
        <AccountCard title="Current plan">
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon} aria-hidden="true"><Icon name="package" size={20} /></div>
            <div className={styles.emptyTitle}>No plan selected yet</div>
            <div className={styles.emptyDesc}>
              Choose a plan to get started. Your free trial starts the day your account is live - you won't be charged today.
            </div>
            <div className={styles.perkRow} style={{ justifyContent: 'center' }}>
              {TRIAL_PERKS.map((p) => (
                <span key={p} className={styles.perk}>
                  <span className={styles.perkIcon}><Icon name="check" size={12} /></span> {p}
                </span>
              ))}
            </div>
            <div className={styles.emptyAction}>
              <button className={styles.btnPrimary} onClick={onChoosePlan}>Choose a plan</button>
            </div>
          </div>
        </AccountCard>
      )}

      {/* ── Quick actions ── */}
      <AccountCard title="Quick actions" description="Jump straight to the things you manage most.">
        <div className={styles.quickGrid}>
          {QUICK_ACTIONS.map((a) => (
            <button key={a.key} className={styles.quickTile} onClick={() => onNavigate(a.key)}>
              <span className={styles.quickArrow}><Icon name="arrowRight" size={15} /></span>
              <span className={styles.quickIcon}><Icon name={a.icon} size={18} /></span>
              <span className={styles.quickBody}>
                <span className={styles.quickTitle}>{a.title}</span>
                <span className={styles.quickDesc}>{a.desc}</span>
              </span>
            </button>
          ))}
        </div>
      </AccountCard>

      {/* ── Profile ── */}
      <AccountCard title="Profile" description="The details we have on file for your account.">
        {hasIdentity ? (
          <>
            <div className={styles.profileHead}>
              <div className={styles.profileAvatar} aria-hidden="true">
                {avatarText || <Icon name="user" size={20} />}
              </div>
              <div style={{ minWidth: 0 }}>
                <div className={styles.profileNameLg}>
                  {name || email || 'Account'}
                  {isFounding && <span className={styles.foundingBadge}>👑 Founding member</span>}
                </div>
                {name && (
                  <div className={styles.profileSub}>{email || 'No email on file'}</div>
                )}
              </div>
            </div>
            <div className={styles.infoGrid}>
              <InfoRow label="Company" icon={<Icon name="building" size={13} />} muted={!identity?.company}>
                {identity?.company || 'Not provided'}
              </InfoRow>
              <InfoRow label="Role" icon={<Icon name="briefcase" size={13} />} muted={!identity?.role}>
                {identity?.role || 'Not provided'}
              </InfoRow>
              {identity?.memberSince && (
                <InfoRow label="Member since" icon={<Icon name="calendar" size={13} />}>
                  {formatDate(identity.memberSince)}
                </InfoRow>
              )}
              <InfoRow label="Account status" icon={<Icon name="activity" size={13} />}>
                {status ? <StatusBadge status={status} /> : <span className={styles.infoValueMuted}>Unknown</span>}
              </InfoRow>
            </div>
          </>
        ) : (
          <EmptyState
            icon={<Icon name="user" size={20} />}
            title="Account details unavailable"
            description="Sign in to view and manage your profile and account information."
          />
        )}
      </AccountCard>

      {/* ── Support ── */}
      <SupportCard onContact={onContact} />
    </>
  );
}
